import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
    User, Calendar, Clock, Euro, LogOut, Edit2, Save, X, 
    Building2, Phone, Mail, CheckCircle, XCircle, AlertCircle,
    ArrowLeft, ChevronDown, ChevronUp, Users, FileText, Ticket
} from 'lucide-react';
import { toast } from "sonner";
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '@/contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UserProfilePage = () => {
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [user, setUser] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [ticketOrders, setTicketOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [expandedBooking, setExpandedBooking] = useState(null);
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [activeTab, setActiveTab] = useState('bookings'); // 'bookings' or 'tickets'

    useEffect(() => {
        checkAuthAndFetch();
    }, []);

    const checkAuthAndFetch = async () => {
        const token = localStorage.getItem('booking_token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            // Fetch user profile
            const userRes = await axios.get(`${API_URL}/api/booking/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(userRes.data);
            setEditForm({
                first_name: userRes.data.first_name || '',
                last_name: userRes.data.last_name || '',
                phone: userRes.data.phone || '',
                company: userRes.data.company || ''
            });

            // Fetch user's bookings
            const bookingsRes = await axios.get(`${API_URL}/api/booking/auth/my-bookings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBookings(bookingsRes.data);
            
            // Fetch user's ticket orders
            const ordersRes = await axios.get(`${API_URL}/api/tickets/orders/my-orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTicketOrders(ordersRes.data);
        } catch (err) {
            console.error('Auth error:', err);
            localStorage.removeItem('booking_token');
            navigate('/login');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('booking_token');
        // Dispatch custom event to notify Navbar immediately
        window.dispatchEvent(new Event('authChange'));
        toast.success('Erfolgreich abgemeldet');
        navigate('/');
    };

    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('booking_token');
            const res = await axios.put(`${API_URL}/api/booking/auth/profile`, editForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(res.data);
            setEditing(false);
            toast.success('Profil aktualisiert');
        } catch (err) {
            toast.error('Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateStr) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('de-DE', { 
                weekday: 'short', 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
            });
        } catch {
            return dateStr;
        }
    };

    const formatTime = (dateStr) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    };

    const getStatusBadge = (booking) => {
        if (booking.status === 'cancelled') {
            return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1">
                <XCircle className="w-3 h-3" /> {t('cancelled')}
            </span>;
        }
        if (booking.is_past) {
            return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> {language === 'de' ? 'Abgeschlossen' : 'Completed'}
            </span>;
        }
        if (booking.status === 'confirmed') {
            return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> {t('confirmed')}
            </span>;
        }
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {t('pending')}
        </span>;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white">
                <Navbar />
                <div className="pt-32 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                </div>
            </div>
        );
    }

    const upcomingBookings = bookings.filter(b => !b.is_past && b.status !== 'cancelled');
    const pastBookings = bookings.filter(b => b.is_past || b.status === 'cancelled');

    return (
        <div className="min-h-screen bg-concrete-50">
            <Navbar />
            
            <main className="pt-28 pb-16 px-4">
                <div className="max-w-4xl mx-auto">
                    {/* Back Button */}
                    <button 
                        onClick={() => navigate('/')}
                        className="flex items-center gap-2 text-concrete-600 hover:text-black mb-6 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>{language === 'de' ? 'Zurück zur Startseite' : 'Back to Homepage'}</span>
                    </button>

                    {/* Profile Card */}
                    <Card className="mb-8 border-0 shadow-lg">
                        <CardHeader className="bg-black text-white rounded-t-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                                        <User className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-xl">
                                            {user?.first_name} {user?.last_name}
                                        </CardTitle>
                                        <p className="text-white/70 text-sm">{user?.email}</p>
                                        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                                            user?.role === 'internal' 
                                                ? 'bg-green-500/20 text-green-300' 
                                                : 'bg-blue-500/20 text-blue-300'
                                        }`}>
                                            {user?.role === 'internal' 
                                                ? (language === 'de' ? 'Interner Nutzer' : 'Internal User')
                                                : (language === 'de' ? 'Externer Nutzer' : 'External User')}
                                        </span>
                                    </div>
                                </div>
                                <Button 
                                    variant="outline" 
                                    onClick={handleLogout}
                                    className="bg-transparent border-white/30 text-white hover:bg-white/10"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    {t('logout')}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-6">
                            {editing ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm text-concrete-600 mb-1 block">{t('first_name')}</label>
                                            <Input
                                                value={editForm.first_name}
                                                onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm text-concrete-600 mb-1 block">{t('last_name')}</label>
                                            <Input
                                                value={editForm.last_name}
                                                onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm text-concrete-600 mb-1 block">{t('phone')}</label>
                                            <Input
                                                value={editForm.phone}
                                                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                                                placeholder="+49..."
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm text-concrete-600 mb-1 block">{t('company')}</label>
                                            <Input
                                                value={editForm.company}
                                                onChange={(e) => setEditForm({...editForm, company: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2 pt-2">
                                        <Button onClick={handleSaveProfile} disabled={saving}>
                                            <Save className="w-4 h-4 mr-2" />
                                            {saving ? (language === 'de' ? 'Speichern...' : 'Saving...') : t('save')}
                                        </Button>
                                        <Button variant="outline" onClick={() => setEditing(false)}>
                                            <X className="w-4 h-4 mr-2" />
                                            {t('cancel')}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-start justify-between">
                                    <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                                        <div className="flex items-center gap-2 text-concrete-600">
                                            <Mail className="w-4 h-4" />
                                            <span>{user?.email}</span>
                                        </div>
                                        {user?.phone && (
                                            <div className="flex items-center gap-2 text-concrete-600">
                                                <Phone className="w-4 h-4" />
                                                <span>{user.phone}</span>
                                            </div>
                                        )}
                                        {user?.company && (
                                            <div className="flex items-center gap-2 text-concrete-600">
                                                <Building2 className="w-4 h-4" />
                                                <span>{user.company}</span>
                                            </div>
                                        )}
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                                        <Edit2 className="w-4 h-4 mr-2" />
                                        {t('edit_profile')}
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Tabs for Bookings and Tickets */}
                    <div className="mb-6">
                        <div className="flex border-b border-concrete-200">
                            <button
                                onClick={() => setActiveTab('bookings')}
                                className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
                                    activeTab === 'bookings'
                                        ? 'border-b-2 border-black text-black'
                                        : 'text-concrete-500 hover:text-black'
                                }`}
                            >
                                <Calendar className="w-4 h-4" />
                                {t('room_bookings')} ({bookings.length})
                            </button>
                            <button
                                onClick={() => setActiveTab('tickets')}
                                className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
                                    activeTab === 'tickets'
                                        ? 'border-b-2 border-black text-black'
                                        : 'text-concrete-500 hover:text-black'
                                }`}
                            >
                                <Ticket className="w-4 h-4" />
                                {t('ticket_purchases')} ({ticketOrders.length})
                            </button>
                        </div>
                    </div>

                    {/* Bookings Tab */}
                    {activeTab === 'bookings' && (
                    <>
                    {/* Upcoming Bookings */}
                    <div className="mb-8">
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            {t('upcoming_bookings')} ({upcomingBookings.length})
                        </h2>
                        
                        {upcomingBookings.length === 0 ? (
                            <Card className="border-dashed border-2 border-concrete-200">
                                <CardContent className="p-8 text-center text-concrete-500">
                                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                    <p>{t('no_bookings')}</p>
                                    <Button 
                                        variant="outline" 
                                        className="mt-4"
                                        onClick={() => navigate('/buchung')}
                                    >
                                        {language === 'de' ? 'Raum buchen' : 'Book a Room'}
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-3">
                                {upcomingBookings.map((booking) => (
                                    <Card 
                                        key={booking.id} 
                                        className="hover:shadow-md transition-shadow cursor-pointer"
                                        onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                                    >
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-black text-white rounded-lg flex flex-col items-center justify-center">
                                                        <span className="text-xs font-medium">
                                                            {new Date(booking.start_time).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { weekday: 'short' })}
                                                        </span>
                                                        <span className="text-lg font-bold leading-none">
                                                            {new Date(booking.start_time).getDate()}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold">{booking.room_name}</p>
                                                        <div className="flex items-center gap-3 text-sm text-concrete-600">
                                                            <span className="flex items-center gap-1">
                                                                <Clock className="w-3 h-3" />
                                                                {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                                                            </span>
                                                            <span>{formatDate(booking.start_time)}</span>
                                                        </div>
                                                        <p className="text-xs text-concrete-400 mt-1 font-mono">
                                                            {language === 'de' ? 'Nr.' : 'No.'} {booking.id?.slice(-8).toUpperCase()}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="text-right">
                                                        <p className="font-bold text-lg">{booking.total_price > 0 ? (booking.total_price * 1.19)?.toFixed(2) : '0,00'} €</p>
                                                        <p className="text-xs text-gray-400">{language === 'de' ? 'brutto' : 'gross'}</p>
                                                        {getStatusBadge(booking)}
                                                    </div>
                                                    {expandedBooking === booking.id 
                                                        ? <ChevronUp className="w-5 h-5 text-concrete-400" />
                                                        : <ChevronDown className="w-5 h-5 text-concrete-400" />
                                                    }
                                                </div>
                                            </div>
                                            
                                            {/* Expanded Details */}
                                            {expandedBooking === booking.id && (
                                                <div className="mt-4 pt-4 border-t border-concrete-200 space-y-3">
                                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                            <span className="text-concrete-500">{t('booking_number')}:</span>
                                                            <p className="font-mono text-xs">{booking.id}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-concrete-500">{t('booked_on')}:</span>
                                                            <p>{booking.created_at ? new Date(booking.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US') : '-'}</p>
                                                        </div>
                                                        {booking.attendees && (
                                                            <div className="flex items-center gap-2">
                                                                <Users className="w-4 h-4 text-concrete-400" />
                                                                <span>{booking.attendees} {t('participants')}</span>
                                                            </div>
                                                        )}
                                                        {booking.purpose && (
                                                            <div className="col-span-2 flex items-start gap-2">
                                                                <FileText className="w-4 h-4 text-concrete-400 mt-0.5" />
                                                                <span>{booking.purpose}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Price breakdown */}
                                                    {booking.total_price > 0 && (
                                                        <div className="bg-gray-50 p-3 rounded-lg text-sm">
                                                            <div className="flex justify-between">
                                                                <span>{t('net')}:</span>
                                                                <span>{booking.total_price?.toFixed(2)} €</span>
                                                            </div>
                                                            <div className="flex justify-between text-gray-500">
                                                                <span>{t('vat')}:</span>
                                                                <span>{(booking.total_price * 0.19)?.toFixed(2)} €</span>
                                                            </div>
                                                            <div className="flex justify-between font-bold border-t border-gray-200 pt-1 mt-1">
                                                                <span>{t('total')}:</span>
                                                                <span>{(booking.total_price * 1.19)?.toFixed(2)} €</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Addons */}
                                                    {booking.selected_addons && booking.selected_addons.length > 0 && (
                                                        <div>
                                                            <span className="text-concrete-500 text-sm">{t('addons')}:</span>
                                                            <ul className="mt-1 text-sm">
                                                                {booking.selected_addons.map((addon, idx) => (
                                                                    <li key={idx} className="flex justify-between">
                                                                        <span>{addon.name} {addon.quantity > 1 ? `(${addon.quantity}x)` : ''}</span>
                                                                        <span>{addon.total_price?.toFixed(2)} €</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Actions */}
                                                    {booking.status === 'confirmed' && !booking.is_past && (
                                                        <div className="pt-2">
                                                            <Button 
                                                                variant="outline" 
                                                                size="sm"
                                                                className="text-red-600 border-red-200 hover:bg-red-50"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/buchung/stornieren?id=${booking.id}`);
                                                                }}
                                                            >
                                                                {t('cancel_booking')}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Past Bookings */}
                    {pastBookings.length > 0 && (
                        <div>
                            <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-concrete-600">
                                <Clock className="w-5 h-5" />
                                {t('past_bookings')} ({pastBookings.length})
                            </h2>
                            
                            <div className="space-y-2">
                                {pastBookings.map((booking) => (
                                    <Card 
                                        key={booking.id} 
                                        className="bg-concrete-50 hover:bg-concrete-100 transition-colors cursor-pointer"
                                        onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                                    >
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 bg-concrete-200 text-concrete-600 rounded-lg flex flex-col items-center justify-center text-sm">
                                                        <span className="text-xs">
                                                            {new Date(booking.start_time).toLocaleDateString('de-DE', { weekday: 'short' })}
                                                        </span>
                                                        <span className="font-bold leading-none">
                                                            {new Date(booking.start_time).getDate()}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{booking.room_name}</p>
                                                        <div className="flex items-center gap-2 text-sm text-concrete-500">
                                                            <span>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</span>
                                                            <span>•</span>
                                                            <span>{formatDate(booking.start_time)}</span>
                                                        </div>
                                                        <p className="text-xs text-concrete-400 mt-0.5 font-mono">
                                                            Nr. {booking.id?.slice(-8).toUpperCase()}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="text-right">
                                                        <p className="font-medium">{booking.total_price > 0 ? (booking.total_price * 1.19)?.toFixed(2) : '0,00'} €</p>
                                                        <p className="text-xs text-gray-400">{language === 'de' ? 'brutto' : 'gross'}</p>
                                                        {getStatusBadge(booking)}
                                                    </div>
                                                    {expandedBooking === booking.id 
                                                        ? <ChevronUp className="w-4 h-4 text-concrete-400" />
                                                        : <ChevronDown className="w-4 h-4 text-concrete-400" />
                                                    }
                                                </div>
                                            </div>
                                            
                                            {/* Expanded Details for past bookings */}
                                            {expandedBooking === booking.id && (
                                                <div className="mt-4 pt-4 border-t border-concrete-200 space-y-2 text-sm">
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <span className="text-concrete-500">Buchungsnummer:</span>
                                                            <p className="font-mono text-xs">{booking.id}</p>
                                                        </div>
                                                        {booking.attendees && (
                                                            <div>
                                                                <span className="text-concrete-500">Teilnehmer:</span>
                                                                <p>{booking.attendees}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {booking.purpose && (
                                                        <div>
                                                            <span className="text-concrete-500">Zweck:</span>
                                                            <p>{booking.purpose}</p>
                                                        </div>
                                                    )}
                                                    {booking.total_price > 0 && (
                                                        <div className="bg-white p-2 rounded text-xs">
                                                            <div className="flex flex-col gap-1">
                                                                <div className="flex justify-between">
                                                                    <span>{t('net')}:</span>
                                                                    <span>{booking.total_price?.toFixed(2)} €</span>
                                                                </div>
                                                                <div className="flex justify-between text-gray-500">
                                                                    <span>{t('vat')}:</span>
                                                                    <span>{(booking.total_price * 0.19)?.toFixed(2)} €</span>
                                                                </div>
                                                                <div className="flex justify-between font-bold border-t pt-1">
                                                                    <span>{t('total')} (brutto):</span>
                                                                    <span>{(booking.total_price * 1.19)?.toFixed(2)} €</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}
                    </>
                    )}

                    {/* Tickets Tab */}
                    {activeTab === 'tickets' && (
                    <div className="space-y-6">
                        {ticketOrders.length === 0 ? (
                            <Card className="border-dashed border-2 border-concrete-200">
                                <CardContent className="p-8 text-center text-concrete-500">
                                    <Ticket className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                    <p>Noch keine Ticket-Käufe</p>
                                    <Button 
                                        variant="outline" 
                                        className="mt-4"
                                        onClick={() => navigate('/tickets')}
                                    >
                                        Events entdecken
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-4">
                                {ticketOrders.map((order) => {
                                    const orderDate = order.created_at ? new Date(order.created_at) : null;
                                    const eventDate = order.event_date ? new Date(order.event_date) : null;
                                    const isPast = eventDate && eventDate < new Date();
                                    const totalTickets = order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
                                    
                                    return (
                                        <Card 
                                            key={order.id}
                                            className={`hover:shadow-md transition-shadow cursor-pointer ${isPast ? 'bg-concrete-50' : ''}`}
                                            onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center ${
                                                            isPast ? 'bg-concrete-200 text-concrete-600' : 'bg-black text-white'
                                                        }`}>
                                                            <Ticket className="w-5 h-5" />
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold">{order.event_name || 'Event'}</p>
                                                            <div className="flex items-center gap-3 text-sm text-concrete-600">
                                                                {eventDate && (
                                                                    <span>{eventDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                                                )}
                                                                <span>•</span>
                                                                <span>{totalTickets} Ticket{totalTickets !== 1 ? 's' : ''}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="text-right">
                                                            <p className="font-bold">{order.total_amount?.toFixed(2)} €</p>
                                                            {order.status === 'paid' ? (
                                                                <span className="text-xs text-green-600 flex items-center gap-1">
                                                                    <CheckCircle className="w-3 h-3" /> Bezahlt
                                                                </span>
                                                            ) : order.status === 'refunded' ? (
                                                                <span className="text-xs text-orange-600 flex items-center gap-1">
                                                                    <XCircle className="w-3 h-3" /> Erstattet
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-yellow-600 flex items-center gap-1">
                                                                    <AlertCircle className="w-3 h-3" /> {order.status}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {expandedOrder === order.id ? (
                                                            <ChevronUp className="w-5 h-5 text-concrete-400" />
                                                        ) : (
                                                            <ChevronDown className="w-5 h-5 text-concrete-400" />
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Expanded Details */}
                                                {expandedOrder === order.id && (
                                                    <div className="mt-4 pt-4 border-t border-concrete-200 space-y-4">
                                                        
                                                        {/* QR Code - Prominent Display (show for paid orders or orders without status) */}
                                                        {!isPast && (order.status === 'paid' || order.status === 'completed' || !order.status) && (
                                                            <div className="bg-black rounded-xl p-6 text-center">
                                                                <div className="bg-white rounded-lg p-4 inline-block mb-4">
                                                                    <QRCodeSVG 
                                                                        value={order.id || 'TICKET'}
                                                                        size={180}
                                                                        level="H"
                                                                        includeMargin={true}
                                                                    />
                                                                </div>
                                                                <p className="text-white font-semibold">Eintrittskarte</p>
                                                                <p className="text-gray-400 text-sm mt-1">QR-Code am Einlass vorzeigen</p>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Order Info */}
                                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                                            <div>
                                                                <span className="text-concrete-500">Bestellnummer:</span>
                                                                <p className="font-mono">{order.id?.slice(0, 8).toUpperCase()}</p>
                                                            </div>
                                                            {orderDate && (
                                                                <div>
                                                                    <span className="text-concrete-500">Bestellt am:</span>
                                                                    <p>{orderDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Ticket Items */}
                                                        <div>
                                                            <span className="text-concrete-500 text-sm">Tickets:</span>
                                                            <ul className="mt-2 space-y-2">
                                                                {order.items?.map((item, idx) => (
                                                                    <li key={idx} className="bg-concrete-50 p-3 rounded-lg">
                                                                        <div className="flex justify-between items-start">
                                                                            <div>
                                                                                <span className="font-medium">{item.ticket_type_name || item.ticket_name}</span>
                                                                                <span className="text-concrete-500 ml-2">x{item.quantity}</span>
                                                                            </div>
                                                                            <span className="font-medium">{(item.total_price || (item.quantity * (item.unit_price || item.price_per_ticket || 0))).toFixed(2)} €</span>
                                                                        </div>
                                                                        {/* Attendees - check both locations */}
                                                                        {(item.attendees && item.attendees.length > 0) && (
                                                                            <div className="mt-2 space-y-1">
                                                                                {item.attendees.map((att, attIdx) => (
                                                                                    <div key={attIdx} className="text-sm text-concrete-600 flex items-center gap-2">
                                                                                        <Users className="w-3 h-3" />
                                                                                        {att.first_name} {att.last_name}
                                                                                        {att.email && <span className="text-concrete-400">({att.email})</span>}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                        
                                                        {/* Past Event Notice */}
                                                        {isPast && (
                                                            <div className="bg-concrete-100 p-3 rounded-lg text-sm text-concrete-600 text-center">
                                                                Dieses Event hat bereits stattgefunden.
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    )}

                    {/* Quick Actions */}
                    <div className="mt-8 pt-8 border-t border-concrete-200">
                        <div className="flex gap-4 justify-center">
                            <Button onClick={() => navigate('/buchung')} className="bg-black hover:bg-black/90">
                                <Calendar className="w-4 h-4 mr-2" />
                                Neue Buchung
                            </Button>
                            <Button variant="outline" onClick={() => navigate('/tickets')}>
                                Tickets kaufen
                            </Button>
                        </div>
                    </div>
                </div>
            </main>
            
            <Footer />
        </div>
    );
};
